import React from "react";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import { ReportData, DetailData } from "../../../../types/analytics";
import { formatNumber } from "./chartConfig";

interface KpiRowProps {
  reportData: ReportData | null;
  detailData: DetailData | null;
  detailLoading: boolean;
}

const KpiCard = ({
  title,
  icon,
  value,
  suffix,
  splits,
  detailLoading,
}: {
  title: string;
  icon: string;
  value: string;
  suffix?: string;
  splits?: { label: string; value: string }[];
  detailLoading?: boolean;
}) => (
  <Card className="shadow-2 h-full">
    <div className="flex align-items-center gap-3 mb-2">
      <i
        className={`pi ${icon} text-2xl`}
        style={{ color: "var(--primary-color)" }}
      />
      <span className="text-500 font-medium">{title}</span>
    </div>
    <div className="text-2xl font-bold text-900">
      {value}
      {suffix && <span className="text-lg font-normal text-500"> {suffix}</span>}
    </div>
    {splits && (
      <div className="flex flex-wrap gap-2 mt-2">
        {detailLoading
          ? [1, 2].map((i) => (
              <Skeleton key={i} width="5rem" height="1rem" />
            ))
          : splits.map((s) => (
              <span key={s.label} className="text-sm text-500">
                {s.value} {s.label}
              </span>
            ))}
      </div>
    )}
  </Card>
);

const KpiRow: React.FC<KpiRowProps> = ({
  reportData,
  detailData,
  detailLoading,
}) => {
  if (!reportData) return null;

  return (
    <div className="grid mb-4">
      <div className="col-12 md:col-6 lg:col-3">
        <KpiCard
          title="Revenue"
          icon="pi-euro"
          value={formatNumber(reportData.totalRevenue)}
          suffix="EUR"
        />
      </div>
      <div className="col-12 md:col-6 lg:col-3">
        <KpiCard
          title="Avg / Session"
          icon="pi-calculator"
          value={formatNumber(reportData.avgPerSession)}
          suffix="EUR"
        />
      </div>
      <div className="col-12 md:col-6 lg:col-3">
        <KpiCard
          title="Sessions"
          icon="pi-bolt"
          value={formatNumber(reportData.totalSessions, 0)}
          detailLoading={detailLoading}
          splits={
            detailData
              ? [
                  { label: "paid", value: String(detailData.paidSessions) },
                  { label: "free", value: String(detailData.freeSessions) },
                ]
              : undefined
          }
        />
      </div>
      <div className="col-12 md:col-6 lg:col-3">
        <KpiCard
          title="Energy"
          icon="pi-bolt"
          value={formatNumber(reportData.totalEnergy)}
          suffix="kWh"
          detailLoading={detailLoading}
          splits={
            detailData
              ? [
                  {
                    label: "paid",
                    value: `${formatNumber(detailData.paidEnergy)} kWh`,
                  },
                  {
                    label: "free",
                    value: `${formatNumber(detailData.freeEnergy)} kWh`,
                  },
                ]
              : undefined
          }
        />
      </div>
    </div>
  );
};

export default KpiRow;
